import { CoinflipChoice, MiniGameStatus } from '@/__generated__/graphql'
import { useSession } from '@/context/SessionProvider'
import { useAppToast } from '@/hooks/useAppToast'
import { SimpleGrid, Button, Box, Flex, Text } from '@chakra-ui/react'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { useMutation } from '@tanstack/react-query'
import { useCoinflipStore } from './CoinflipProvider'
import { GAME_MODE } from './types'
import { Image } from '@/components/HOC/Image'
import CoinHeadImage from '@/public/assets/images/coinflip/coin-head.png'
import CoinTailImage from '@/public/assets/images/coinflip/coin-tail.png'
import { NextRoundPayableAmount } from './PayableAmount'
import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { memo } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { COINFLIP_CONTRACT_ADDRESS } from '@/constants/constants'
import { getCoinflipGameResult } from '@/services/api-game'
import { useAppModal } from '@/context/ModalProvider'
import { placeBetTransaction } from '@/services/GameBroadcaster'

export const HeadsOrTailsButtons = memo(() => {
  const { t } = useTranslation()
  const { account, signIn, sendTransaction } = useSession()
  const {
    gameId,
    gameConfig,
    setGameId,
    setNextStep,
    handleEndGame,
    roundState,
    setGameActive,
    startAnimation,
    setAnimationSide,
    setDrawing,
    setDrawingOff,
    view: { isDrawing },
    isWithdrawing,
    setError,
    addToHistory,
    form,
  } = useCoinflipStore()
  const { onError } = useAppToast()
  const modal = useAppModal()
  const { play } = useAudio()

  const { mutate } = useMutation({
    mutationFn: async (side: CoinflipChoice) => {
      if (!account) {
        signIn()
        return
      }

      setGameActive()
      setDrawing()
      startAnimation()

      const gameTx = !gameId
        ? await handleStart(side)
        : await handleNextRound({ side, step: roundState.currentStep, gameId })

      if ('gameConfig' in gameTx) {
        useCoinflipStore.setState({
          gameConfig: gameTx.gameConfig,
        })
      }

      const getResult =
        gameTx.game_result || (await getCoinflipGameResult(gameTx!.gameId, gameTx!.game_step, gameTx!.random_round))

      const result = getResult === 'tails' ? CoinflipChoice.Tails : CoinflipChoice.Heads
      setAnimationSide(result)

      useCoinflipStore.setState({
        lastRoundResult: result === side ? MiniGameStatus.Win : MiniGameStatus.Lose,
      })

      if (result != side) {
        addToHistory({
          id: gameTx!.gameId + '-' + gameTx!.game_step,
          choice: result,
          result: result === side ? MiniGameStatus.Win : MiniGameStatus.Lose,
        })
        handleEndGame()
        return
      }

      if (form.fields.mode === GAME_MODE.MULTIPLY) {
        setGameId(gameTx.gameId)
        setNextStep(Number(gameTx?.game_step) + 1, result)
        setDrawingOff()
        addToHistory({
          id: gameTx!.gameId + '-' + gameTx!.game_step,
          choice: result,
          result: result === side ? MiniGameStatus.Win : MiniGameStatus.Lose,
        })
        return
      }

      addToHistory({
        id: gameTx!.gameId + '-' + gameTx!.game_step,
        choice: result,
        result: result === side ? MiniGameStatus.Win : MiniGameStatus.Lose,
      })
      handleEndGame()
    },
    onError: () => {
      setError()
      if (form.fields.mode === GAME_MODE.INSTANT || roundState.currentStep === 0) {
        handleEndGame()
      }
      onError({ custom: { title: t('coinflip.unable-to-process') } })
    },
  })

  const handleStart = async (side: string) => {
    const tx = await placeBetTransaction({
      contract_addr: COINFLIP_CONTRACT_ADDRESS,
      funds: {
        denom: form.fields.denom,
        amount: form.fields.amount,
      },
      msg: {
        mode: form.fields.mode,
        side: side.toLowerCase(),
      },
    })

    if (!tx || tx?.code != 0) {
      throw Error()
    }

    const gameEvent = tx.events?.find((e) => e.type === 'wasm-coinflip_v1_game_created')
    let maxGameRound = gameEvent?.attributes?.find((a: any) => a.key === 'max_round')?.value
    let maxWin = gameEvent?.attributes?.find((a: any) => a.key === 'max_win')?.value
    const rtp = gameEvent?.attributes?.find((a: any) => a.key === 'rtp')?.value

    const event = tx.events?.find((e) => e.type === 'wasm-coinflip_v1_game_round_created')
    const gameId = event?.attributes?.find((a: any) => a.key === 'game_id')?.value
    const random_round = event?.attributes?.find((a: any) => a.key === 'random_round')?.value
    const game_step = event?.attributes?.find((a: any) => a.key === 'game_step')?.value

    const game_result = event?.attributes?.find((a: any) => a.key === 'internal_game_result')?.value

    return {
      gameId,
      random_round,
      game_step,
      game_result,

      gameConfig: {
        rtp,
        maxWin,
        maxGameRound,
      },
    }
  }

  const handleNextRound = async ({ side, step, gameId }: { side: string; step: number; gameId: string }) => {
    const tx = await sendTransaction(
      MsgExecuteContractCompat.fromJSON({
        sender: account?.injectiveAddress!,
        contractAddress: COINFLIP_CONTRACT_ADDRESS,
        funds: [],
        msg: {
          next_round: {
            game_id: Number(gameId),
            side: side.toLowerCase(),
            step,
          },
        },
      }),
      { showSuccess: false }
    )

    if (!tx || tx?.code != 0) {
      throw Error()
    }

    const event = tx.events?.find((e) => e.type === 'wasm-coinflip_v1_game_round_created')
    const random_round = event?.attributes?.find((a: any) => a.key === 'random_round')?.value
    const game_step = event?.attributes?.find((a: any) => a.key === 'game_step')?.value

    const game_result = event?.attributes?.find((a: any) => a.key === 'internal_game_result')?.value

    return {
      gameId,
      random_round,
      game_step,
      game_result,
    }
  }

  const canContinue = gameConfig.maxGameRound > roundState.currentStep

  const isDisabled = isDrawing || isWithdrawing || !canContinue || (!!form.errors.length && !gameId)

  return (
    <SimpleGrid columns={2} gap={2} mx={'auto'} mb={{ base: 5, md: '70px' }}>
      <Button
        justifySelf={'end'}
        h={{ base: '54px', md: '62px' }}
        w={{ base: '112px', md: 40 }}
        fontSize={{ base: 'md', md: 'xl' }}
        pl={{ base: '27px', md: '31px' }}
        pr={2}
        isDisabled={isDisabled}
        roundedRight={'2xl'}
        roundedLeft={'none'}
        onMouseEnter={() => !isDisabled && play(SOUNDS.SET_AMOUNT)}
        onClick={() => mutate(CoinflipChoice.Heads)}
        pos={'relative'}
      >
        <Box
          rounded={'full'}
          overflow={'hidden'}
          pos={'absolute'}
          h={{ base: '54px', md: '62px' }}
          w={{ base: '54px', md: '62px' }}
          left={{ base: '-27px', md: '-31px' }}
        >
          <Image style={{ height: '62px', width: '62px' }} src={CoinHeadImage} alt="" />
        </Box>
        <Flex flexDir={'column'}>
          <Text transition={'0.2s'}>{t('coinflip.heads')}</Text>
          {canContinue && <NextRoundPayableAmount />}
        </Flex>
      </Button>
      <Button
        bg={'#CB71F6'}
        bgGradient={'linear-gradient(99deg, #E383FB 5.35%, #A352EE 61.4%);'}
        _hover={{ boxShadow: '0px 4px 48px 0px rgba(203, 113, 246, 0.60)', bg: '#CB71F6' }}
        _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
        h={{ base: '54px', md: '62px' }}
        w={{ base: '112px', md: 40 }}
        fontSize={{ base: 'md', md: 'xl' }}
        pr={{ base: '27px', md: '31px' }}
        pl={2}
        isDisabled={isDisabled}
        roundedLeft={'2xl'}
        roundedRight={'none'}
        pos={'relative'}
        onMouseEnter={() => !isDisabled && play(SOUNDS.SET_AMOUNT)}
        onClick={() => mutate(CoinflipChoice.Tails)}
      >
        <Flex flexDir={'column'}>
          <Text transition={'0.2s'}>{t('coinflip.tails')}</Text>
          {canContinue && <NextRoundPayableAmount />}
        </Flex>
        <Box
          rounded={'full'}
          overflow={'hidden'}
          pos={'absolute'}
          h={{ base: '54px', md: '62px' }}
          w={{ base: '54px', md: '62px' }}
          right={{ base: '-27px', md: '-31px' }}
        >
          <Image style={{ height: '62px', width: '62px' }} src={CoinTailImage} alt="" />
        </Box>
      </Button>
    </SimpleGrid>
  )
})
